import React, { useState, useRef, useEffect } from 'react';
import { FaUser, FaRobot, FaTools, FaInfoCircle } from 'react-icons/fa';
import { FiChevronDown, FiChevronUp, FiSend } from 'react-icons/fi';
import toast from 'react-hot-toast';

import { fetchWithHandling, API_URL } from '../../services/api';

// Helper to render chat message icons
const RoleIcon = ({ role }) => {
  switch (role) {
    case 'user':
      return <FaUser className="h-4 w-4 flex-shrink-0 text-blue-400" />;
    case 'assistant':
      return <FaRobot className="h-4 w-4 flex-shrink-0 text-green-400" />;
    case 'tool':
      return <FaTools className="h-4 w-4 flex-shrink-0 text-yellow-400" />;
    default:
      return null;
  }
};

// Component to render ASSISTANT tool calls (requests)
const ToolCall = ({ toolCall }) => {
  const [isOpen, setIsOpen] = useState(false);
  let args = {};
  try {
    args = JSON.parse(toolCall.function.arguments);
  } catch (e) {
    args = { raw: toolCall.function.arguments };
  }

  return (
    <div className="ml-6 mt-2 rounded-md border border-zinc-700 bg-zinc-800 p-2 text-xs">
      {/* eslint-disable-next-line */}
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <FaTools className="text-indigo-400" />
          <span className="font-mono font-semibold text-indigo-300">
            {toolCall.function.name}
          </span>
        </div>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </div>
      {isOpen && (
        <pre className="mt-2 whitespace-pre-wrap break-all rounded-md bg-zinc-900 p-2">
          {JSON.stringify(args, null, 2)}
        </pre>
      )}
    </div>
  );
};

// Renders the TOOL role's output (responses)
const ToolResponse = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);

  let displayContent;
  try {
    const parsed = JSON.parse(content);
    // Ensure it's a structured object/array, not just a JSON string literal like "some string"
    if (parsed && typeof parsed === 'object') {
      displayContent = JSON.stringify(parsed, null, 2);
    } else {
      // It was valid JSON but not an object (e.g., a string or number), so display as is.
      displayContent = String(content);
    }
  } catch (e) {
    // Not a JSON string, so display as is.
    displayContent = String(content);
  }

  return (
    // eslint-disable-next-line
    <div className="mt-2 rounded-md border border-zinc-700 bg-zinc-800 p-2 text-xs">
      {/* eslint-disable-next-line */}
      <div
        className="flex cursor-pointer items-center justify-between"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <FaTools className="text-yellow-400" />
          <span className="font-mono font-semibold text-yellow-300">
            Tool Output
          </span>
        </div>
        {isOpen ? <FiChevronUp /> : <FiChevronDown />}
      </div>
      {isOpen && (
        <pre className="mt-2 whitespace-pre-wrap break-all rounded-md bg-zinc-900 p-2">
          {displayContent}
        </pre>
      )}
    </div>
  );
};

// Main Chat UI Component
const ChatUI = ({
  chats,
  nodeId,
  workflowId,
  executionId,
  userGoogleId,
  status,
  workflowStatus,
}) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  // eslint-disable-next-line
  const filteredChats =
    chats?.filter((chat) =>
      ['assistant', 'user', 'tool'].includes(chat.role),
    ) || [];

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [filteredChats]);

  const handleSendPrompt = async () => {
    if (!prompt.trim()) return;
    setIsLoading(true);
    try {
      const response = await fetchWithHandling(
        `${API_URL}/canvas/workflow/executions/${executionId}/nodes/${nodeId}/prompt`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${userGoogleId}`,
          },
          body: JSON.stringify({
            prompt,
            workflow_id: workflowId,
            execution_id: executionId,
          }),
        },
      );
      toast.success(response.message || 'Prompt sent!');
      setPrompt(''); // Clear input on success
    } catch (error) {
      toast.error('Failed to send prompt.');
    } finally {
      setIsLoading(false);
    }
  };

  // --- MODIFICATION IS HERE ---
  // The check now includes the overall workflow status
  const canSendPrompt =
    (status === 'success' || status === 'failed') &&
    (workflowStatus === 'success' || workflowStatus === 'failed');

  return (
    <div className="flex h-full max-h-[70vh] flex-col">
      {/* Messages Area */}
      <div className="custom-scrollbar flex-grow space-y-4 overflow-y-auto pr-2">
        {filteredChats.length > 0 ? (
          filteredChats.map((chat) => (
            <div key={chat._id} className="flex items-start gap-3 text-sm">
              <RoleIcon role={chat.role} />
              <div className="flex-1">
                <span className="font-bold capitalize text-zinc-400">
                  {chat.role}
                </span>

                {/* Render standard text content for user and assistant */}
                {chat.role !== 'tool' && chat.content && (
                  <p className="whitespace-pre-wrap text-zinc-200">
                    {Array.isArray(chat.content)
                      ? chat.content.map((b) => b.text || '').join('')
                      : chat.content}
                  </p>
                )}

                {/* Render assistant's tool calls (requests) */}
                {chat.tool_calls &&
                  chat.tool_calls.map((tc) => (
                    <ToolCall key={tc.id} toolCall={tc} />
                  ))}

                {/* Render the tool's output (response) using the new component */}
                {chat.role === 'tool' && chat.content != null && (
                  <ToolResponse content={chat.content} />
                )}
              </div>
            </div>
          ))
        ) : (
          <div className="pt-10 text-center text-zinc-500">
            No conversation history for this node.
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Prompt Input Area */}
      {canSendPrompt ? (
        <div className="mt-4 flex items-center gap-2 border-t border-zinc-700 pt-4">
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Send a follow-up prompt..."
            className="text-p-text w-full resize-none rounded-md border border-zinc-600 bg-zinc-800 px-3 py-2 text-sm transition focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            rows="1"
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSendPrompt();
              }
            }}
          />
          <button
            onClick={handleSendPrompt}
            disabled={isLoading || !prompt.trim()}
            className="flex-shrink-0 rounded-md bg-blue-600 p-2 text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-zinc-600"
          >
            {isLoading ? (
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
            ) : (
              <FiSend className="h-5 w-5" />
            )}
          </button>
        </div>
      ) : (
        // This block is shown when the user cannot send a prompt
        <div className="mt-4 flex items-center justify-center border-t border-zinc-700 pt-4 text-center">
          <div className="flex items-center gap-2 rounded-md bg-zinc-800 p-3 text-xs text-zinc-400">
            <FaInfoCircle className="flex-shrink-0" />
            <span>
              Chat is available once the node and workflow are complete.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatUI;
